import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import FilterPanel from './FilterPanel';
import { CardFilterStateApi } from './cardFilterUrlState';
import { COLOR_PIPS } from './CardFilterTokenRow';
import { useDrawerLayout } from './useDrawerLayout';
import { SearchFilterState, normalizeColors } from 'screens/Main/Search/searchState';
import './CardFilterRail.css';

export {
  FILTER_RAIL_WIDTH_PX,
  FILTER_STRIP_WIDTH_PX,
  TRADE_SIDEBAR_WIDTH_PX,
  DRAWER_PUSH_CLASS,
} from './useDrawerLayout';

export interface CardFilterRailProps {
  /** The list surfaces' URL-backed state; Search passes state/onChange/onClearAll explicitly. */
  filters?: CardFilterStateApi;
  state?: SearchFilterState;
  onChange?: (patch: Partial<SearchFilterState>) => void;
  onClearAll?: () => void;
  headerTooltip?: { heading: string; body: string };
  /** Own lists and profile tabs omit the Trade section. */
  hideTradeSection?: boolean;
  /** The trade cart is open on this page (profile tabs). Drives the push/auto-collapse rule. */
  cartOpen?: boolean;
  /** Search's "Hide filters" toolbar control: the column collapses to nothing. */
  hidden?: boolean;
  /** Search's arrival-coachmark pass-throughs. */
  dimmed?: boolean;
  tooltipSuppressed?: boolean;
  tradeGroupSlot?: React.ReactNode;
}

const ArrowGlyph: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`card-filter-strip-arrow${open ? ' open' : ''}`}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    aria-hidden="true"
  >
    <path d="M8 2 L4 6 L8 10" fill="none" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

/**
 * THE filter rail, for every surface: Search, My Want/Have List, the profile
 * tabs and Recent Activity consume this one component.
 *
 * The model is the Search page's: a full-height opaque column pinned to the
 * right edge of the viewport, the topmost thing in its column, with the page
 * header and the content region ending at its left edge. The column's width
 * is the single reactive value (--rail-width, written by useDrawerLayout):
 * 344 open, 48 as the pip strip, 0 hidden. The header's right edge, the cart's
 * slot and the content padding all read the same value, so they cannot drift.
 *
 * Inside the column, FilterPanel keeps its own pinned FILTERS row (sticky at
 * the panel's top) and scrolls its sections internally, exactly as on Search.
 *
 * Portalled to <body>: on the profile page a transformed ancestor would turn
 * position:fixed into local positioning (measured 2026-09-08).
 */
export const CardFilterRail: React.FC<CardFilterRailProps> = ({
  filters,
  state,
  onChange,
  onClearAll,
  headerTooltip,
  hideTradeSection = false,
  cartOpen = false,
  hidden = false,
  dimmed,
  tooltipSuppressed,
  tradeGroupSlot,
}) => {
  const panelState = state ?? filters!.state;
  const panelOnChange = onChange ?? filters!.update;
  const panelOnClearAll = onClearAll ?? filters!.clearAll;

  const layout = useDrawerLayout({ cartOpen, railMounted: !hidden });
  const [overlayOpen, setOverlayOpen] = useState(false);

  // The overlay exists only while the rail is auto-collapsed.
  useEffect(() => {
    if (!layout.autoCollapsed) setOverlayOpen(false);
  }, [layout.autoCollapsed]);

  useEffect(() => {
    if (!overlayOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOverlayOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [overlayOpen]);

  const onStripClick = () => {
    if (layout.autoCollapsed) setOverlayOpen((o) => !o);
    else layout.toggleCollapsed();
  };

  const activeColors = normalizeColors(panelState.colors);
  const setCount = panelState.setName.trim() ? 1 : 0;
  const isStrip = layout.railMode === 'strip';

  const panel = (extra?: { onCollapse?: () => void }) => (
    <FilterPanel
      state={panelState}
      onChange={panelOnChange}
      onClearAll={panelOnClearAll}
      hideTradeSection={hideTradeSection}
      headerTooltip={headerTooltip}
      dimmed={dimmed}
      tooltipSuppressed={tooltipSuppressed}
      tradeGroupSlot={tradeGroupSlot}
      onCollapse={extra?.onCollapse}
    />
  );

  return createPortal(
    <>
      <aside
        className="right-rail"
        data-mode={layout.railMode}
        data-testid="card-filter-rail"
        aria-hidden={layout.railMode === 'none'}
      >
        <div className="right-rail-panel" aria-hidden={isStrip}>
          {panel({ onCollapse: layout.toggleCollapsed })}
        </div>

        <button
          type="button"
          className={`card-filter-strip${overlayOpen ? ' open' : ''}${layout.autoCollapsed ? ' card-filter-strip--auto' : ''}`}
          onClick={onStripClick}
          tabIndex={isStrip ? 0 : -1}
          aria-hidden={!isStrip}
          aria-expanded={layout.autoCollapsed ? overlayOpen : !layout.collapsedPref}
          aria-label={layout.autoCollapsed ? (overlayOpen ? 'Close filters' : 'Open filters') : 'Expand filters'}
          data-testid="card-filter-strip"
        >
          <ArrowGlyph open={overlayOpen} />
          <span className="card-filter-strip-label">FILTERS</span>
          {activeColors.length > 0 && (
            <span className="card-filter-strip-pips" aria-label={`Active colors ${activeColors.join(' ')}`}>
              {activeColors.map((color) => (
                <img key={color} src={COLOR_PIPS[color]?.icon} alt="" aria-hidden="true" />
              ))}
            </span>
          )}
          {setCount > 0 && (
            <span className="card-filter-strip-badge" aria-label={`${setCount} set filter active`}>
              {setCount}×
            </span>
          )}
        </button>
      </aside>

      {overlayOpen && (
        <>
          {/* Scrim: a click anywhere on the page closes the overlay and does
              nothing else; the row underneath is not activated. */}
          <div
            className="card-filter-scrim"
            onClick={() => setOverlayOpen(false)}
            data-testid="card-filter-overlay-backdrop"
          />
          <div className="card-filter-overlay" data-testid="card-filter-overlay">
            <button
              type="button"
              className="card-filter-overlay-close"
              onClick={() => setOverlayOpen(false)}
              aria-label="Close filters"
            >
              ×
            </button>
            {panel()}
          </div>
        </>
      )}
    </>,
    document.body,
  );
};

export default CardFilterRail;
